import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ModusListModule } from '@trimble-gcs/modus';
import { FileDragDropDirective } from '@trimble-gcs/ngx-common';
import { v4 as uuidv4 } from 'uuid';
import { ALLOWED_EXTENSIONS, ImportFile } from '../../import.models';
import { allowImportForFileType } from '../../import.service';
import { PickerType } from '../file-picker.models';
import { localFilePickerTranslations } from './local-file-picker.translations';

@Component({
  selector: 'sd-local-file-picker',
  imports: [CommonModule, FileDragDropDirective, ReactiveFormsModule, ModusListModule],
  templateUrl: './local-file-picker.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocalFilePickerComponent {
  private readonly browse = viewChild.required<ElementRef<HTMLInputElement>>('browse');

  readonly translations = localFilePickerTranslations();
  readonly allowedExtensions = ALLOWED_EXTENSIONS;

  readonly disabled = input<boolean>(false);
  readonly filesPicked = output<ImportFile[]>();

  readonly dragOver = signal(false);

  filesDropped(droppedFiles: File[]) {
    const importFiles = droppedFiles
      .filter((file) => allowImportForFileType(file.name))
      .map((file) => this.mapToImportFile(file));

    this.filesPicked.emit(importFiles);
  }

  browseClick() {
    if (this.disabled()) return;

    this.browse().nativeElement.click();
  }

  filesSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const fileList = Array.from(target.files ?? []);
    if (fileList.length === 0) return;

    // clear the input, otherwise if the same file is picked the change event won't fire.
    target.value = '';

    const importFiles = fileList
      .filter((file) => allowImportForFileType(file.name))
      .map((file) => this.mapToImportFile(file));

    this.filesPicked.emit(importFiles);
  }

  private mapToImportFile(file: File): ImportFile {
    return {
      id: uuidv4(),
      name: file.name,
      size: file.size,
      pickerType: PickerType.Upload,
      fileUpload: { file },
    };
  }
}
